/**
 * Lot Sorting Operations
 *
 * Handles sorting lots by various criteria with temporal awareness.
 *
 * **Close Time Sorting:**
 * - Ascending (default): Most urgent lots first (soonest to close)
 *   - Past dates come before future dates (oldest past → newest past → soonest future → farthest future)
 *   - This is the natural sort order for auctions - users see what needs attention NOW
 * - Descending: Reverse order (farthest future dates first)
 *
 * @module portal/modules/lotOperations/sorting
 */

import { state, SORT_FIELDS, DEFAULT_SORT_FIELD } from '../state';
import { Lot } from '../types';
import { getUpdatedTimestamp } from '../utils';

/**
 * Parse a date string for sorting purposes.
 * If the date has no time component, assume 11:59 PM for sorting.
 */
function parseDateForSorting(dateStr: string): number | null {
  if (typeof dateStr !== 'string' || !dateStr.trim()) return null;

  // Try parsing as-is first
  const asIs = Date.parse(dateStr);
  if (Number.isFinite(asIs)) {
    // Check if this is a date-only value (YYYY-MM-DD format)
    // by seeing if it matches the ISO date pattern without time
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr.trim())) {
      // Set to 11:59:59.999 PM on that date for sorting purposes
      const date = new Date(asIs);
      date.setHours(23, 59, 59, 999);
      return date.getTime();
    }
    return asIs;
  }

  return null;
}

/**
 * Get effective close time for a lot
 */
function getEffectiveCloseTime(lot: Lot): number {
  // If we have a numeric closeAt, use it
  if (typeof lot.closeAt === 'number' && Number.isFinite(lot.closeAt)) {
    return lot.closeAt;
  }

  // For lots with live finish date range, try to parse closeDateTime
  if ((lot.liveFinish || lot.liveFinishDateRange) && lot.closeDateTime) {
    const closeStr =
      typeof lot.closeDateTime === 'string'
        ? lot.closeDateTime
        : lot.closeDateTime?.iso || lot.closeDateTime?.raw;
    const parsed = closeStr ? parseDateForSorting(closeStr) : null;
    if (parsed) return parsed;
  }

  // Try parsing closeDateTime as-is
  if (lot.closeDateTime) {
    const closeStr =
      typeof lot.closeDateTime === 'string'
        ? lot.closeDateTime
        : lot.closeDateTime?.iso || lot.closeDateTime?.raw;
    const parsed = closeStr ? parseDateForSorting(closeStr) : null;
    if (parsed) return parsed;
  }

  return 0;
}

/**
 * Compare two lots by updated timestamp (descending)
 */
function compareByUpdatedDesc(a: Lot, b: Lot): number {
  const aTs = getUpdatedTimestamp(a) || 0;
  const bTs = getUpdatedTimestamp(b) || 0;
  return bTs - aTs;
}

/**
 * Sort lots based on current sort settings
 *
 * For closeAt field:
 * - 'asc' = Temporal ascending = Soonest/most urgent first (natural auction order)
 * - 'desc' = Temporal descending = Farthest/least urgent first
 *
 * The implementation uses (aClose - bClose) which means:
 * - Smaller timestamps (earlier dates) come first when asc
 * - This includes past dates before future dates
 */
export function sortLots(lots: Lot[]): Lot[] {
  if (!Array.isArray(lots)) return [];
  const field = state.sort && state.sort.field ? state.sort.field : DEFAULT_SORT_FIELD;
  const direction = state.sort && state.sort.direction === 'asc' ? 'asc' : 'desc';
  const multiplier = direction === 'asc' ? 1 : -1;

  return lots.sort((a, b) => {
    if (field === SORT_FIELDS.CLOSE) {
      // Get effective close times (which handles date ranges)
      const aCloseRaw = getEffectiveCloseTime(a);
      const bCloseRaw = getEffectiveCloseTime(b);

      // Convert 0 to undefined for proper handling
      const aClose = aCloseRaw || undefined;
      const bClose = bCloseRaw || undefined;

      // Sort undefined values to the end
      if (aClose === undefined && bClose === undefined) return compareByUpdatedDesc(a, b);
      if (aClose === undefined) return 1;
      if (bClose === undefined) return -1;

      const diff = aClose - bClose;
      if (diff !== 0) return diff * multiplier;
      return compareByUpdatedDesc(a, b);
    }

    // Default: sort by updated
    const aTs = getUpdatedTimestamp(a) || 0;
    const bTs = getUpdatedTimestamp(b) || 0;
    return (bTs - aTs) * multiplier;
  });
}

// Re-export sort field constants for consumers
export { SORT_FIELDS } from '../state';
